import React, { useState } from 'react';

const PhotoCard = ({ photo, index, isAdmin, isSelectionMode, isSelected, onImageClick, onDelete, onToggleSelect }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={`
        relative group overflow-hidden rounded-sm cursor-pointer aspect-[3/4] shadow-sm transition-all duration-200
        ${isSelected ? 'ring-4 ring-blue1 scale-[0.98]' : 'hover:shadow-md'}
        bg-gray-100
      `}
      onClick={() => {
        if (isSelectionMode) {
          onToggleSelect(photo.id);
        } else {
          onImageClick(index);
        }
      }}
    >
      {/* Shimmer loader */}
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%]" />
      )}

      {/* Admin Delete/Select Controls */}
      {isAdmin && (
        <div className="absolute top-2 right-2 z-30 flex gap-2">
          <div
            className={`
              bg-white rounded-full p-1 shadow-md cursor-pointer transition-opacity
              ${isSelectionMode || isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(photo.id);
            }}
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-blue1 bg-blue1' : 'border-gray-300'}`}>
              {isSelected && (
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>

          {!isSelectionMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(photo.id, photo.storagePath, photo.thumbStoragePath);
              }}
              className="bg-red-600 text-white p-2 rounded-full shadow-md hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100"
              title="Delete photo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Image */}
      <img
        src={photo.thumbUrl || photo.url}
        alt={photo.title}
        className={`w-full h-full object-cover transition-all duration-500 ${!isSelectionMode && 'group-hover:brightness-90'} ${loaded ? 'opacity-100' : 'opacity-0'}`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
      />

      {/* Overlay Bar */}
      {!isSelectionMode && (
        <div className="absolute bottom-0 left-0 right-0 bg-blue p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
          {photo.title && (
            <h3 className="text-white font-['IBM_Plex_Serif'] text-lg font-medium truncate mb-0.5">
              {photo.title}
            </h3>
          )}
          <p className="text-white/90 font-['IBM_Plex_Serif'] text-sm font-light truncate">
            {photo.title ? photo.guestName : `Uploaded by ${photo.guestName}`}
          </p>
        </div>
      )}
    </div>
  );
};

const GalleryGrid = ({ photos, onImageClick, isAdmin, onDelete, isSelectionMode, selectedPhotos, onToggleSelect, noImagesText }) => {

  if (photos.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 font-['IBM_Plex_Serif'] text-xl">{noImagesText || ""}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-[1200px] mx-auto px-4">
      {photos.map((photo, index) => (
        <PhotoCard
          key={photo.id}
          photo={photo}
          index={index}
          isAdmin={isAdmin}
          isSelectionMode={isSelectionMode}
          isSelected={selectedPhotos.has(photo.id)}
          onImageClick={onImageClick}
          onDelete={onDelete}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
};

export default GalleryGrid;
